import type { ISnowplowUm } from '@fanduel/snowplow-um-contract';
import { SnowplowUm } from '@fanduel/snowplow-um';
import { CoreEvents } from '@fanduel/cpe-um-bundle/core-events/library';

import config from '@/config';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import {
  isIosWrapper,
  isRunningInsideWrapper,
} from '@/utilities/helpers/wrapperHelpers';
import { initializeUnifiedModules } from '@/unified-modules/unified-modules-core';

export type SnowplowConfig = {
  appId: string;
  androidAppId: string;
  trackerUrl: string;
  trackerId?: string;
};

let snowplowInstance: ISnowplowUm | null = null;
let initPromise: Promise<ISnowplowUm> | null = null;

/**
 * Synchronous accessor for the cached Snowplow instance.
 * Returns null if `initializeSnowplow()` has not yet resolved.
 */
export const getSnowplowInstance = (): ISnowplowUm | null => snowplowInstance;

export const initializeSnowplow = async (): Promise<ISnowplowUm> => {
  if (snowplowInstance) {
    return snowplowInstance;
  }

  if (initPromise !== null) {
    return initPromise;
  }

  initPromise = (async () => {
    try {
      await initializeUnifiedModules();
      await CoreEvents.initialize();

      const snowplowConfig: SnowplowConfig | undefined = config.snowplow;

      if (snowplowConfig) {
        SnowplowUm.initialize();

        const isAndroid = isRunningInsideWrapper() && !isIosWrapper();
        const appId = isAndroid
          ? snowplowConfig.androidAppId
          : snowplowConfig.appId;

        SnowplowUm.postInitialize({ ...snowplowConfig, appId });
      }

      snowplowInstance = SnowplowUm;
      return SnowplowUm;
    } catch (error) {
      Logger.logRumError({
        name: '[SNOWPLOW][CLIENT]snowplow_init_failed',
        description: `[SNOWPLOW][CLIENT]snowplow_init_failed: ${error}`,
        useCase: DdEventCategory.GameLaunch,
        eventType: DdEventType.Error,
      });
      initPromise = null;
      throw error;
    }
  })();

  return initPromise;
};

/** Test-only helper to reset module-level singleton state between tests. */
export const __resetSnowplowClientForTests = () => {
  snowplowInstance = null;
  initPromise = null;
};
